import { ArrowDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';

type DataTableType = {
  itemOptions?: number[];
  header?: {
    name: string;
    sortingAllowed: boolean;
    type: string;
  }[];
  data?: any[];
};

export default function DataTableAnalytics({
  itemOptions = [10],
  header = [{ name: '', sortingAllowed: true, type: '' }],
  data = []
}: DataTableType) {
  const [pageNumber, setPageNumber] = useState(1);
  const [itemPerPage, setItemsPerPage] = useState(itemOptions[0]);
  const [tableRows, setTableRows] = useState<NonNullable<DataTableType['data']>>([]);
  const [filterName, setFilterName] = useState('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | ''>(''); // new state

  const totalPages = Math.ceil(data?.length / itemPerPage);
  const visibileTableContent = data?.slice(
    itemPerPage * (pageNumber - 1),
    pageNumber * itemPerPage
  );

  // --- Utility functions ---
  function convertValue(value: string | number) {
    if (typeof value === 'string') {
      value = value.replace('€', '').replace('K', '000').replace('%', '');
      const num = parseFloat(value);
      return isNaN(num) ? value : num;
    }
    return value;
  }

  function sortDataAscending(data: any[], key: string) {
    return [...data].sort((a, b) => {
      const valA = convertValue(a[key]);
      const valB = convertValue(b[key]);
      if (valA < valB) return -1;
      if (valA > valB) return 1;
      return 0;
    });
  }

  function sortDataDescending(data: any[], key: string) {
    return [...data].sort((a, b) => {
      const valA = convertValue(a[key]);
      const valB = convertValue(b[key]);
      if (valA > valB) return -1;
      if (valA < valB) return 1;
      return 0;
    });
  }

  // --- Apply sorting effect ---
  useEffect(() => {
    const currentTable = data.slice(itemPerPage * (pageNumber - 1), pageNumber * itemPerPage);

    if (!filterName || !sortOrder) {
      setTableRows(currentTable);
      setTableRows(currentTable);
      return;
    }

    const sorted =
      sortOrder === 'asc'
        ? sortDataAscending(currentTable, filterName)
        : sortDataDescending(currentTable, filterName);

    setTableRows(sorted);
  }, [filterName, sortOrder, pageNumber, itemPerPage, data]);

  // --- Handle filter click ---
  const handleFilter = (name: string) => {
    if (filterName !== name) {
      setFilterName(name);
      setSortOrder('asc');
    } else {
      if (sortOrder === 'asc') setSortOrder('desc');
      else if (sortOrder === 'desc') {
        setFilterName('');
        setSortOrder('');
      } else setSortOrder('asc');
    }
  };

  return (
    <table className="table bg-danger">
      <thead>
        <tr>
          {header?.map((hdr, i) => (
            <td
              key={i + 'td'}
              scope="col"
              style={{
                height: '64px',
                background: 'rgba(235, 242, 254, 1)',
                verticalAlign: 'middle',
                textAlign: 'left'
              }}>
              {hdr?.name}
              {hdr?.sortingAllowed && (
                <ArrowDown
                  onClick={() => handleFilter(hdr?.type)}
                  style={{
                    width: '16px',
                    height: '16px',
                    cursor: 'pointer',
                    marginLeft: '6px',
                    color: filterName === hdr?.type ? (sortOrder ? 'black' : 'gray') : 'gray',
                    transform:
                      filterName === hdr?.type && sortOrder === 'desc'
                        ? 'rotate(180deg)'
                        : 'rotate(0deg)',
                    transition: 'transform 0.2s ease'
                  }}
                />
              )}
            </td>
          ))}
        </tr>
      </thead>
      <tbody style={{ maxHeight: '288px' }}>
        {tableRows?.map((rw, i) => (
          <tr
            key={i + 'tr'}
            style={{
              height: '55px',
              verticalAlign: 'middle',
              borderBottom: '1px solid rgba(191, 215, 251, 1)'
            }}>
            {header?.map((item) => (
              <td key={item?.name}>{rw?.[item?.type]}</td>
            ))}
          </tr>
        ))}
      </tbody>
      <tfoot style={{ width: '100%' }}>
        <tr style={{ height: '64px' }}>
          <td
            colSpan={header?.length}
            style={{
              verticalAlign: 'middle',
              backgroundColor: 'rgba(235, 242, 254, 1)',
              width: '200px'
            }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <span style={{ color: 'rgba(113, 113, 122, 1)' }}>Total: </span> {tableRows?.length}
              </div>
              <div>
                {' '}
                <ChevronLeft
                  style={{
                    cursor: 'pointer',
                    color: pageNumber === 1 ? 'rgba(113, 113, 122, 1)' : 'var(--uni_std_clr)',
                    width: '16px'
                  }}
                  onClick={() => {
                    pageNumber !== 1 && setPageNumber(pageNumber - 1);
                  }}
                />
                <span
                  style={{
                    width: '50px',
                    borderBottom: '1px solid var(--uni_std_clr)',
                    color: 'var(--uni_std_clr)',
                    margin: '0 20px',
                    padding: '0 10px'
                  }}>
                  {pageNumber}{' '}
                </span>
                of{' '}
                <span
                  style={{
                    width: '50px',
                    margin: '0 20px',
                    padding: '0 10px'
                  }}>
                  {totalPages}{' '}
                </span>
                <ChevronRight
                  style={{
                    cursor: 'pointer',
                    color:
                      pageNumber === totalPages ? 'rgba(113, 113, 122, 1)' : 'var(--uni_std_clr)'
                  }}
                  onClick={(prev) => {
                    pageNumber !== totalPages && setPageNumber(pageNumber + 1);
                  }}
                />
              </div>
              <div>
                {' '}
                <form action="">
                  <label htmlFor="pages" style={{ color: 'rgba(113, 113, 122, 1)' }}>
                    Show Per Page:
                  </label>
                  <select
                    name="pages"
                    id="pages"
                    style={{
                      border: 'none',
                      backgroundColor: 'rgba(235, 242, 254, 1)',
                      cursor: 'pointer'
                    }}
                    onChange={(e) => setItemsPerPage(parseInt(e.target.value))}>
                    {itemOptions?.map((pageItems, index) => (
                      <option value={pageItems} key={pageItems + index}>
                        {pageItems}
                      </option>
                    ))}
                  </select>
                </form>
              </div>
            </div>
          </td>
        </tr>
      </tfoot>
    </table>
  );
}
